import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getBookingsQueryDetails,
  postChatBooking,
  updateBookingsQueryDetails,
} from "../../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch, RootState } from "redux/store";
import { useLocation, useNavigate } from "react-router-dom";
import successHandler from "utils/helpers/SuccessHandler";
import { useSelector } from "react-redux";
import ErrorHandler from "utils/helpers/ErrorHandler";
import moment from "moment";
import MessageList from "containers/Messages/MessageList";
import { getMessage } from "../../../redux/reducers/UserSlice/UserSlice";
import "../../Messages/MessageList.scss";
import BookingDetailsSkeleton from "shared/Skelton/CommonSkelton";
import { toast } from "react-toastify";

// Query-specific components (relative imports from ./components)
import QueryProgressTracker from "./components/QueryProgressTracker";
import QueryActions from "./components/QueryActions";
import QueryInfoSummary from "./components/QueryInfoSummary";
import QueryPropertyCard from "./components/QueryPropertyCard";

// Shared components from BookingRequest
import UserProfileCard from "../../BookingRequest/components/UserProfileCard";
import ReplySection from "../../BookingRequest/components/ReplySection";

export interface BookingQueryDetailsProps {
  className?: string;
}

const BookingQueryDetails: FC<BookingQueryDetailsProps> = ({
  className = "",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const [bookingRequestDetails, setBookingRequestDetails] = useState<
    any | null
  >(null);
  const [replyText, setReplyText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { globalId } = useSelector((state: RootState) => state.property);
  const bookingId = globalId || id;
  const [loading, setLoading] = useState<boolean>(true);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const chatId = bookingRequestDetails?.booking_query?.chat?.id;
  const modifiedBookingMap = chatMessages;
  const [showMore, setShowMore] = useState(false);
  const [previews, setPreviews] = useState<any[]>([]);


const checkInDate = moment.utc(
  bookingRequestDetails?.booking_query?.check_in_date || new Date()
).local().toDate();

const checkOutDate = moment.utc(
  bookingRequestDetails?.booking_query?.check_out_date || new Date()
).local().toDate();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);

    // Process each file for preview
    newFiles.forEach((file) => {
      const type = file.type;

      if (type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            {
              type: "image",
              content: reader.result as string,
              name: file.name,
            },
          ]);
        };
        reader.readAsDataURL(file);
      } else if (type === "application/pdf") {
        setPreviews((prev) => [...prev, { type: "pdf", name: file.name }]);
      } else if (type.startsWith("video/")) {
        const maxSizeInBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
          toast.error("Video file size should not exceed 5 MB");
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            {
              type: "video",
              content: reader.result as string,
              name: file.name,
            },
          ]);
        };
        reader.readAsDataURL(file);
      } else if (
        type === "application/msword" ||
        type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        type === "application/vnd.ms-word.document.macroEnabled.12"
      ) {
        setPreviews((prev) => [...prev, { type: "doc", name: file.name }]);
      } else if (
        type === "application/vnd.ms-excel" ||
        type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        type === "application/vnd.ms-excel.sheet.macroEnabled.12"
      ) {
        setPreviews((prev) => [...prev, { type: "excel", name: file.name }]);
      } else if (
        type === "application/vnd.ms-powerpoint" ||
        type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
        type === "application/vnd.ms-powerpoint.presentation.macroEnabled.12"
      ) {
        setPreviews((prev) => [
          ...prev,
          { type: "powerpoint", name: file.name },
        ]);
      } else if (type === "text/plain" || type === "text/csv") {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews((prev) => [
            ...prev,
            { type: "text", content: reader.result as string, name: file.name },
          ]);
        };
        reader.readAsText(file);
      } else if (
        type === "application/zip" ||
        type === "application/x-zip-compressed"
      ) {
        setPreviews((prev) => [...prev, { type: "zip", name: file.name }]);
      } else {
        setPreviews((prev) => [
          ...prev,
          {
            type: "generic",
            name: file.name,
            extension: file.name.split(".").pop() || "",
          },
        ]);
      }
    });
  };

  
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const fetchAllManageBooking = async () => {
  console.log("🔍 Fetching booking query ID:", id);
  const action = await dispatch(getBookingsQueryDetails(id));
  console.log("📡 Full API Response:", action?.payload);
  console.log("🏠 Property Data:", action?.payload?.booking_query?.property);
  console.log("📊 Property Keys:", action?.payload?.booking_query?.property ? Object.keys(action?.payload?.booking_query?.property) : "No property");
  if (action?.payload?.success) {
    setBookingRequestDetails(action?.payload);
  }
};

  const fetchMessages = async (pge: number) => {
    const data = {
      page: pge,
      chatId: chatId,
    };
    setShowMore(true);

    try {
      const response = await dispatch(getMessage(data)).unwrap();
      setChatMessages(response || []);
    } catch (err: any) {
      console.error("Error fetching messages:", err);
    } finally {
      setShowMore(false);
    }
  };

  useEffect(() => {
    if (chatId) {
      fetchMessages(+1);
    }
  }, [chatId]);

  const handleSubmit = async () => {
    if (!replyText.trim() && selectedFiles.length === 0) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("message[body]", replyText);

      selectedFiles.forEach((file) => {
        formData.append("message[attachments][]", file);
      });

      await dispatch(
        postChatBooking({
          chatId: bookingRequestDetails?.booking_query?.chat?.id,
          formData,
        })
      ).unwrap();
      setReplyText("");
      setSelectedFiles([]);
      setPreviews([]);
      fetchAllManageBooking();
      fetchMessages(1);
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateBooking = async () => {
    const id = bookingId;
    const propertyId = bookingRequestDetails?.booking_query?.property?.slug;
    const state = "booking_invitation";

    try {
      const action = await dispatch(
        updateBookingsQueryDetails({ id, propertyId, state })
      );

      if (updateBookingsQueryDetails.fulfilled.match(action)) {
        successHandler("Booking Invitation Send successfully!");
        fetchAllManageBooking();
        fetchMessages(1);
      } else {
        ErrorHandler("Failed to update booking state.");
      }
    } catch (error) {
      ErrorHandler("Error updating booking state.");
    }
  };

  const handleDeclineBooking = async () => {
    const id = bookingId;
    const propertyId = bookingRequestDetails?.booking_query?.property?.slug;
    const state = "rejected";

    try {
      const action = await dispatch(
        updateBookingsQueryDetails({ id, propertyId, state })
      );

      if (updateBookingsQueryDetails.fulfilled.match(action)) {
        successHandler("Booking Rejected successfully!");
        fetchAllManageBooking();
        fetchMessages(1);
      } else {
        ErrorHandler("Failed to Decline booking state.");
      }
    } catch (error) {
      ErrorHandler("Error updating booking state.");
    }
  };

  const handleBookingRequest = async () => {
    const id = bookingId;
    const propertyId = bookingRequestDetails?.booking_query?.property?.slug;
    const state = "accepted";
    try {
      const action = await dispatch(
        updateBookingsQueryDetails({ id, propertyId, state })
      );
      if (updateBookingsQueryDetails.fulfilled.match(action)) {
        successHandler("Booking Request Send successfully!");
        navigate(`/booking_request?id=${action.payload.booking.id}`);
      } else {
        ErrorHandler("Failed to Send booking.");
      }
    } catch (error) {
      ErrorHandler("Failed to Send booking.");
    }
  };

  const handleDateSubmit = async (dates: any) => {
    const id = bookingId;
    const propertyId = bookingRequestDetails?.booking_query?.property?.slug;
    const state = "request_change";
    const check_in_date = dates?.startDate;
    const check_out_date = dates?.endDate;

    try {
      const action = await dispatch(
        updateBookingsQueryDetails({
          id,
          propertyId,
          state,
          check_in_date,
          check_out_date,
        })
      );

      if (updateBookingsQueryDetails.fulfilled.match(action)) {
        successHandler("Booking date changed successfully!");
        await dispatch(getBookingsQueryDetails(id));
        fetchMessages(1);
      } else {
        ErrorHandler("Failed to change the date.");
      }
    } catch (error) {
      ErrorHandler("Failed to send booking request.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchAllManageBooking();
      setLoading(false);
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (
      bookingRequestDetails?.booking_query?.state === "accepted" &&
      currentUser?.is_host
    ) {
      navigate(`/booking_request?id=${bookingRequestDetails?.booking?.id}`);
    }
  }, [bookingRequestDetails, navigate]);

  if (loading) {
    return <BookingDetailsSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto p-4 lg:container">
      {/* Header with distinct blue theme */}
      <div className="mb-6 pb-4 border-b-2 border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-blue-600 rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Booking Inquiry
            </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
  {currentUser?.is_host ? (
    // Host view
    <>
      <span className="text-blue-600 dark:text-blue-400">Inquiry from</span>{" "}
      <span className="font-medium text-gray-900 dark:text-gray-100">
        {bookingRequestDetails?.booking_query?.chat?.sender?.name || 
         bookingRequestDetails?.booking_query?.user?.name || 
         "Guest"}
      </span>
    </>
  ) : (
    // Guest view
    <>
      <span className="text-blue-600 dark:text-blue-400">Your inquiry to</span>{" "}
      <span className="font-medium text-gray-900 dark:text-gray-100">
        {bookingRequestDetails?.booking_query?.chat?.receiver?.name || 
         bookingRequestDetails?.booking_query?.property_user?.name || 
         "Host"}
      </span>
    </>
  )}
</p>
          </div>
        </div>
      </div>

      <div className="mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Main Content Area */}
        <div className="md:col-span-2 mb-8">
          <div className="lg:col-span-2 space-y-5">
            {/* Progress Tracker */}
            <QueryProgressTracker
              currentState={bookingRequestDetails?.booking_query?.state}
              isHost={currentUser?.is_host || false}
            />

            {/* Main Card */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 md:p-8 border border-gray-200 dark:border-gray-800 shadow-lg">
              {/* Title Section */}
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {bookingRequestDetails?.booking_query?.property?.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Inquiry for {moment(checkInDate).format("MMM DD, YYYY")} to{" "}
                  {moment(checkOutDate).format("MMM DD, YYYY")}
                </p>
              </div>

              <hr className="border-gray-200 dark:border-gray-700 mb-6" />

              {/* Query Info Summary */}
              <QueryInfoSummary
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                numberOfMonths={
                  bookingRequestDetails?.booking_query?.no_of_months
                }
                numberOfGuests={
                  bookingRequestDetails?.booking_query?.guest_count
                }
                isSharedProperty={
                  bookingRequestDetails?.booking_query?.is_shared_property ===
                    true ||
                  bookingRequestDetails?.booking_query?.is_shared_property ===
                    "true"
                }
                bookedRoom={bookingRequestDetails?.booking_query?.booked_room}
                propertyTitle={
                  bookingRequestDetails?.booking_query?.property?.title
                }
                totalRooms={
                  bookingRequestDetails?.booking_query?.property?.rooms_count
                }
              />

              {/* Actions Section */}
              <div className="mt-6">
                <QueryActions
                  queryState={bookingRequestDetails?.booking_query?.state}
                  isHost={currentUser?.is_host || false}
                  receiverName={
                    bookingRequestDetails?.booking_query?.chat?.receiver
                      ?.name || "Host"
                  }
                  senderName={
                    bookingRequestDetails?.booking_query?.chat?.sender?.name ||
                    "Guest"
                  }
                  propertySlug={
                    bookingRequestDetails?.booking_query?.property?.slug
                  }
                  bookingRequestDetails={bookingRequestDetails}
                  onInviteToBook={handleUpdateBooking}
                  onDeclineQuery={handleDeclineBooking}
                  onBookingRequest={handleBookingRequest}
                  onDateChange={handleDateSubmit}
                />
              </div>

              {/* Reply Section */}
              <div className="mt-8">
                <ReplySection
                  replyText={replyText}
                  setReplyText={setReplyText}
                  selectedFiles={selectedFiles}
                  previews={previews}
                  isSubmitting={isSubmitting}
                  handleFileChange={handleFileChange}
                  handleRemoveFile={handleRemoveFile}
                  handleSubmit={handleSubmit}
                />
              </div>

              {/* Messages Section */}
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
                <MessageList
                  modifiedBookingMap={modifiedBookingMap}
                  loadMoreMessages={fetchMessages}
                  showMore={showMore}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* User Profile Card */}
          {currentUser?.is_host ? (
            <UserProfileCard
              user={bookingRequestDetails?.booking_query?.user}
              isHost={false}
              bookingStatus={bookingRequestDetails?.booking_query?.state}
              userLabel="Guest"
            />
          ) : (
            <UserProfileCard
              user={bookingRequestDetails?.booking_query?.property_user}
              isHost={true}
              bookingStatus={bookingRequestDetails?.booking_query?.state}
              userLabel="Host"
            />
          )}

          {/* Property Card */}
          <QueryPropertyCard
            property={bookingRequestDetails?.booking_query?.property}
            bookingQuery={bookingRequestDetails?.booking_query}
            isSharedProperty={
              bookingRequestDetails?.booking_query?.is_shared_property ===
                true ||
              bookingRequestDetails?.booking_query?.is_shared_property ===
                "true"
            }
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingQueryDetails;
